/**
 * SimilarProductsSection — Intelligent product recommendations for PDP.
 * Shows premium alternative (if any) plus a grid of similar products
 * that share dimensions but differ in variant fields.
 */
import { useMemo } from 'react';
import { findSimilarProducts, getPremiumAlternative } from '@services/recommendationService';

/** Human-readable labels for variant diff fields */
const VARIANT_LABELS = {
  carbideGrade: 'Carbide grade',
  buttonShape: 'Button shape',
  faceDesign: 'Face design',
  skirtDesign: 'Skirt design',
  bitProductType: 'Bit type',
};

export default function SimilarProductsSection({ product, allProducts, userState, onNavigate }) {
  const similarProducts = useMemo(
    () => findSimilarProducts(product, allProducts || []),
    [product, allProducts]
  );

  const premiumAlt = useMemo(
    () => getPremiumAlternative(product, allProducts || []),
    [product, allProducts]
  );

  // Hide section entirely when nothing to show
  if (similarProducts.length === 0 && !premiumAlt) return null;

  return (
    <section className="mt-8" aria-labelledby="similar-products-heading">
      <h2
        id="similar-products-heading"
        className="font-sandvik-headline text-lg font-normal text-carbon-black mb-4"
      >
        Similar products
      </h2>

      {/* --- Premium alternative card --- */}
      {premiumAlt && (
        <div className="border-2 border-electric-blue p-4 mb-4 bg-blue-50/30">
          <span className="text-xs font-medium text-electric-blue tracking-wide mb-1 block">
            Premium alternative
          </span>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 flex-shrink-0 bg-n1 rounded flex items-center justify-center overflow-hidden">
              {premiumAlt.imageUrl ? (
                <img
                  src={premiumAlt.imageUrl}
                  alt={premiumAlt.description || premiumAlt.itemCode}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-n5 text-xs font-sandvik-text">No image</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sandvik-text text-carbon-black text-sm line-clamp-2">
                {premiumAlt.description || premiumAlt.itemCode}
              </p>
              {premiumAlt.diameterMm && (
                <p className="text-xs text-n5 font-sandvik-text mt-1">
                  {premiumAlt.diameterMm} mm
                </p>
              )}
              <button
                type="button"
                onClick={() => onNavigate(premiumAlt.itemCode)}
                className="text-electric-blue text-sm font-sandvik-text hover:underline mt-2 inline-block"
              >
                View details ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Similar products grid --- */}
      {similarProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {similarProducts.map((p) => (
            <div
              key={p.itemCode}
              className="border border-n2 p-4 bg-white hover:shadow-md transition-shadow"
            >
              {/* Image area */}
              <div className="h-[120px] bg-n1 rounded flex items-center justify-center mb-3 overflow-hidden">
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.description || p.itemCode}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-n5 text-xs font-sandvik-text">No image</span>
                )}
              </div>

              {/* Description */}
              <p className="font-sandvik-text text-carbon-black text-sm line-clamp-2 mb-2">
                {p.description || p.itemCode}
              </p>

              {/* Variant differences */}
              {Array.isArray(p._variantDiffs) && p._variantDiffs.length > 0 && (
                <div className="space-y-1 mb-3">
                  {p._variantDiffs.map((diff) => (
                    <p key={diff.field} className="text-xs text-n6 font-sandvik-text">
                      <span className="font-medium text-carbon-black">
                        {VARIANT_LABELS[diff.field] || diff.field}:{' '}
                      </span>
                      {diff.altValue}
                    </p>
                  ))}
                </div>
              )}

              {/* Navigate link */}
              <button
                type="button"
                onClick={() => onNavigate(p.itemCode)}
                className="text-electric-blue text-sm font-sandvik-text hover:underline"
              >
                View details ›
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
